import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FolderKanban,
  Cpu,
  ClipboardCheck,
  FileWarning,
  Activity,
  ArrowRight,
  FileText,
  Clock,
} from 'lucide-react'
import { dashboardApi } from '../services/dashboardApi'
import { projectApi } from '../services/projectApi'
import { searchApi, type OverdueData } from '../services/searchApi'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { ProjectInsights } from '../components/ai/ProjectInsights'
import type { DashboardStats, Project } from '../types'

function DonutChart({ data, size = 120, centerLabel, centerValue }: {
  data: { label: string; value: number; color: string }[]
  size?: number
  centerLabel?: string
  centerValue?: string | number
}) {
  const total = data.reduce((s, d) => s + d.value, 0)
  if (total === 0) return <p className="text-muted text-center">No data</p>

  const radius = 40
  const circumference = 2 * Math.PI * radius
  let offset = 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--spacing-3)' }}>
      <svg width={size} height={size} viewBox="0 0 100 100">
        {data.filter(d => d.value > 0).map((d, i) => {
          const segmentLength = (d.value / total) * circumference
          const currentOffset = offset
          offset += segmentLength
          return (
            <circle
              key={i}
              cx="50" cy="50" r={radius}
              fill="none"
              stroke={d.color}
              strokeWidth="16"
              strokeDasharray={`${segmentLength} ${circumference - segmentLength}`}
              strokeDashoffset={-currentOffset}
              transform="rotate(-90 50 50)"
              style={{ transition: 'stroke-dasharray 0.5s ease, stroke-dashoffset 0.5s ease' }}
            />
          )
        })}
        {centerValue !== undefined && (
          <>
            <text x="50" y="46" textAnchor="middle" fill="var(--ise-text-primary, #fff)" fontSize="18" fontWeight="700">{centerValue}</text>
            {centerLabel && <text x="50" y="60" textAnchor="middle" fill="var(--ise-text-secondary, #999)" fontSize="8">{centerLabel}</text>}
          </>
        )}
      </svg>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2) var(--spacing-3)', justifyContent: 'center' }}>
        {data.filter(d => d.value > 0).map(d => (
          <div key={d.label} className="d-flex align-center gap-1" style={{ fontSize: 'var(--font-size-xs)' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
            <span className="text-secondary-color">{d.label}</span>
            <span className="font-medium">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function Dashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [overdue, setOverdue] = useState<OverdueData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [selectedProject, setSelectedProject] = useState('')

  const loadStats = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = selectedProject
        ? await dashboardApi.getProjectSpecificStats(selectedProject)
        : await dashboardApi.getGlobalStats()
      setStats(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard')
    } finally {
      setLoading(false)
    }
  }, [selectedProject])

  useEffect(() => { loadStats() }, [loadStats])

  useEffect(() => {
    projectApi.getAll({}).then(r => setProjects(r.items)).catch(() => {})
    searchApi.getOverdue().then(setOverdue).catch(() => {})
  }, [])

  if (loading) return <LoadingSpinner message="Loading dashboard..." />

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1>Dashboard</h1>
            <p className="page-subtitle">Commissioning overview</p>
          </div>
        </div>
        <div className="info-card danger">
          <p style={{ margin: 0, color: 'var(--accent-red)' }}>{error}</p>
          <button className="btn btn-sm btn-secondary mt-3" onClick={loadStats}>Retry</button>
        </div>
      </div>
    )
  }

  if (!stats) return null

  return (
    <div className="page-container animate-fade-in">
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p className="page-subtitle">
            {selectedProject
              ? `Project overview — ${(projects.find(p => String(p.id) === selectedProject) as unknown as { projectName?: string; name?: string })?.projectName || (projects.find(p => String(p.id) === selectedProject) as unknown as { name?: string })?.name || ''}`
              : 'Commissioning management overview'}
          </p>
        </div>
        <div className="page-header-actions">
          <select
            className="form-select"
            value={selectedProject}
            onChange={e => setSelectedProject(e.target.value)}
            style={{ minWidth: 200 }}
          >
            <option value="">All Projects</option>
            {projects.map(p => {
              const proj = p as unknown as { id: string | number; projectNumber?: string; projectName?: string; name?: string }
              return (
                <option key={proj.id} value={String(proj.id)}>
                  {proj.projectNumber ? `${proj.projectNumber} — ` : ''}{proj.projectName || proj.name}
                </option>
              )
            })}
          </select>
        </div>
      </div>

      <div className="metrics-row mb-6">
        <div className="metric-card blue">
          <div className="metric-header">
            <span className="metric-label">Active Projects</span>
            <div className="metric-icon blue">
              <FolderKanban size={20} />
            </div>
          </div>
          <div className="metric-value">{stats.activeProjects}</div>
          <div className="metric-subtext">Currently in progress</div>
        </div>

        <div className="metric-card cyan">
          <div className="metric-header">
            <span className="metric-label">Total Equipment</span>
            <div className="metric-icon cyan">
              <Cpu size={20} />
            </div>
          </div>
          <div className="metric-value">{stats.totalEquipment}</div>
          <div className="metric-subtext">Across {stats.totalSystems} systems</div>
        </div>

        <div className="metric-card green">
          <div className="metric-header">
            <span className="metric-label">Checklist Completion</span>
            <div className="metric-icon green">
              <ClipboardCheck size={20} />
            </div>
          </div>
          <div className="metric-value">{Math.round(stats.checklistCompletion)}%</div>
          <div className="metric-subtext">{stats.completedChecklists} of {stats.totalChecklists} complete</div>
        </div>

        <div className="metric-card amber">
          <div className="metric-header">
            <span className="metric-label">Open Issues</span>
            <div className="metric-icon amber">
              <FileWarning size={20} />
            </div>
          </div>
          <div className="metric-value">{stats.openIssues}</div>
          <div className="metric-subtext">{stats.totalIssues} total issues</div>
        </div>
      </div>

      {overdue && overdue.counts.issues > 0 && (
        <div className="card mb-6" style={{ borderLeft: '4px solid var(--accent-red)' }}>
          <div className="card-header">
            <div className="d-flex align-center gap-2">
              <Clock size={18} style={{ color: 'var(--accent-red)' }} />
              <h3 style={{ color: 'var(--accent-red)' }}>Overdue Items ({overdue.counts.issues})</h3>
            </div>
          </div>
          <div className="card-body" style={{ padding: 'var(--spacing-3) var(--spacing-5)' }}>
            <div>
              <div className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                <FileWarning size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                Issues ({overdue.counts.issues})
              </div>
              {overdue.overdueIssues.slice(0, 5).map((item) => (
                <div key={item.id} className="d-flex align-center gap-2 mb-1" style={{ fontSize: 'var(--font-size-sm)' }}>
                  <span className="badge badge-gray-light" style={{ fontSize: '10px', background: 'var(--accent-red-soft)', color: 'var(--accent-red)' }}>
                    {item.priority}
                  </span>
                  <span className="text-accent" style={{ flexShrink: 0 }}>{item.issueNumber}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</span>
                  <span className="text-muted" style={{ fontSize: 'var(--font-size-xs)', flexShrink: 0 }}>
                    Due {new Date(item.dueDate).toLocaleDateString()}
                  </span>
                </div>
              ))}
              {overdue.overdueIssues.length > 5 && (
                <button className="btn btn-ghost btn-xs mt-1" onClick={() => navigate('/issues')}>
                  +{overdue.overdueIssues.length - 5} more
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <ProjectInsights />
      </div>

      <div className="dashboard-grid">
        <div className="card">
          <div className="card-header">
            <h3>Issues by Status</h3>
          </div>
          <div className="card-body">
            <div className="punch-status-bars">
              {[
                { label: 'Open', count: stats.issuesByStatus.open, color: 'var(--accent-red)' },
                { label: 'In Progress', count: stats.issuesByStatus.inProgress, color: 'var(--accent-blue)' },
                { label: 'Pending Response', count: stats.issuesByStatus.pendingResponse, color: 'var(--accent-amber)' },
                { label: 'Resolved', count: stats.issuesByStatus.resolved, color: 'var(--accent-green)' },
                { label: 'Closed', count: stats.issuesByStatus.closed, color: 'var(--text-muted)' },
                { label: 'Deferred', count: stats.issuesByStatus.deferred, color: 'var(--accent-purple)' },
              ].map((item) => (
                <div key={item.label} className="punch-status-row">
                  <div className="punch-status-label">
                    <span style={{ color: item.color, fontWeight: 600 }}>{item.label}</span>
                    <span className="text-secondary-color">{item.count}</span>
                  </div>
                  <div className="progress-bar-track" style={{ height: '8px' }}>
                    <div
                      className="progress-bar-fill"
                      style={{
                        width: stats.totalIssues > 0 ? `${(item.count / stats.totalIssues) * 100}%` : '0%',
                        backgroundColor: item.color,
                        height: '100%',
                        borderRadius: 'var(--radius-full)',
                        transition: 'width 0.5s ease',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Checklists by Status</h3>
          </div>
          <div className="card-body">
            <DonutChart
              data={[
                { label: 'Complete', value: stats.completedChecklists, color: 'var(--accent-green)' },
                { label: 'Remaining', value: stats.totalChecklists - stats.completedChecklists, color: 'var(--accent-blue)' },
              ]}
              centerValue={`${Math.round(stats.checklistCompletion)}%`}
              centerLabel="complete"
            />
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Issues by Category</h3>
          </div>
          <div className="card-body">
            {(() => {
              const categoryColors: Record<string, string> = {
                Design: 'var(--accent-purple)',
                Installation: 'var(--accent-blue)',
                Controls: 'var(--accent-cyan)',
                TAB: 'var(--accent-amber)',
                Commissioning: 'var(--accent-green)',
                Safety: 'var(--accent-red)',
                Documentation: 'var(--text-muted)',
                Other: 'var(--border-light)',
              }
              const entries = Object.entries(stats.issuesByCategory || {}).filter(([, v]) => v > 0)
              if (entries.length === 0) {
                return <p className="text-muted text-center" style={{ padding: 'var(--spacing-4) 0' }}>No issues recorded</p>
              }
              return (
                <DonutChart
                  data={entries.map(([cat, cnt]) => ({
                    label: cat,
                    value: cnt,
                    color: categoryColors[cat] || 'var(--border-light)',
                  }))}
                  centerValue={stats.totalIssues}
                  centerLabel="total"
                />
              )
            })()}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Recent Activity</h3>
            <Activity size={20} className="text-muted" />
          </div>
          <div className="card-body">
            {stats.recentActivity.length === 0 ? (
              <p className="text-muted text-center" style={{ padding: 'var(--spacing-6) 0' }}>
                No recent activity
              </p>
            ) : (
              <div className="activity-feed">
                {stats.recentActivity.slice(0, 8).map((entry) => (
                  <div key={entry.id} className="activity-item">
                    <div className="activity-dot" />
                    <div className="activity-content">
                      <p className="activity-text">
                        <strong>{entry.action}</strong> {entry.description}
                      </p>
                      <span className="activity-meta">
                        {entry.performedBy} - {new Date(entry.performedAt).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="page-section mt-6">
        <div className="page-section-header">
          <h3 className="page-section-title">Quick Actions</h3>
        </div>
        <div className="content-grid content-grid-auto">
          <button className="card card-clickable" onClick={() => navigate('/projects')}>
            <div className="card-body d-flex align-center gap-3">
              <FolderKanban size={24} className="text-accent" />
              <div className="flex-1">
                <div className="font-medium">View Projects</div>
                <div className="text-sm text-muted">Manage commissioning projects</div>
              </div>
              <ArrowRight size={16} className="text-muted" />
            </div>
          </button>
          <button className="card card-clickable" onClick={() => navigate('/checklists')}>
            <div className="card-body d-flex align-center gap-3">
              <ClipboardCheck size={24} className="text-success" />
              <div className="flex-1">
                <div className="font-medium">Checklists</div>
                <div className="text-sm text-muted">Manage templates and execute</div>
              </div>
              <ArrowRight size={16} className="text-muted" />
            </div>
          </button>
          <button className="card card-clickable" onClick={() => navigate('/issues')}>
            <div className="card-body d-flex align-center gap-3">
              <FileWarning size={24} style={{ color: 'var(--accent-amber)' }} />
              <div className="flex-1">
                <div className="font-medium">Issues Log</div>
                <div className="text-sm text-muted">Track deficiencies, RFIs, observations</div>
              </div>
              <ArrowRight size={16} className="text-muted" />
            </div>
          </button>
          <button className="card card-clickable" onClick={() => navigate('/cx-report')}>
            <div className="card-body d-flex align-center gap-3">
              <FileText size={24} style={{ color: 'var(--accent-purple)' }} />
              <div className="flex-1">
                <div className="font-medium">Cx Report</div>
                <div className="text-sm text-muted">Generate AI-powered commissioning report</div>
              </div>
              <ArrowRight size={16} className="text-muted" />
            </div>
          </button>
        </div>
      </div>
    </div>
  )
}
